import { useMutation, useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { queryKeys } from '@shared/lib/queryKeys';
import { offlineDataManager } from '@shared/lib/offlineData';
import { offlineQueueManager } from '@shared/lib/offlineQueue';
import { Task, TaskLink, TaskLinkType } from '@shared/types';

const genLocalId = (): string => {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return `local-${rand}`;
};

// Refresh both task caches (archived / active) from local storage.
const refreshTaskCaches = (qc: QueryClient) => {
  const all = offlineDataManager.getTasks();
  qc.setQueryData(queryKeys.tasks.list(true), all);
  qc.setQueryData(queryKeys.tasks.list(false), all.filter(t => !t.is_archived));
};

// Offline-first: serve tasks from local storage. Manual sync refreshes them.
export function useTasksQuery(includeArchived = false) {
  return useQuery({
    queryKey: queryKeys.tasks.list(includeArchived),
    queryFn: async () => {
      const all = offlineDataManager.getTasks();
      return includeArchived ? all : all.filter(t => !t.is_archived);
    },
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    networkMode: 'always',
    retry: false,
  });
}

// Offline-first: create the task locally + queue it for manual sync.
export function useCreateTaskMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Parameters<typeof api.createTask>[0]) => {
      const now = new Date().toISOString();
      const localTask: Task = {
        task_id: genLocalId(),
        user_id: '',
        org_id: payload.org_id ?? null,
        note_id: payload.note_id ?? null,
        title: payload.title,
        description: payload.description ?? null,
        status: (payload.status as Task['status']) || 'TODO',
        is_archived: false,
        sequence: 0,
        due_date: payload.due_date ?? null,
        created_at: now,
        updated_at: now,
        assignees: [],
        links: [],
      };
      offlineDataManager.addTask(localTask);
      offlineQueueManager.addOperation({ type: 'CREATE_TASK', data: localTask, maxRetries: 3 });
      return localTask;
    },
    onSuccess: () => refreshTaskCaches(qc),
  });
}

// Offline-first: update the task locally + queue it (covers status move, archive, edits).
export function useUpdateTaskMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, payload }: { taskId: string; payload: Parameters<typeof api.updateTask>[1] }) => {
      const updates: Partial<Task> = { ...(payload as Partial<Task>), updated_at: new Date().toISOString() };
      offlineDataManager.updateTask(taskId, updates);
      offlineQueueManager.addOperation({ type: 'UPDATE_TASK', data: { task_id: taskId, payload }, maxRetries: 3 });
      return offlineDataManager.getTasks().find(t => t.task_id === taskId) as Task;
    },
    onSuccess: () => refreshTaskCaches(qc),
  });
}

// Offline-first: delete the task locally + queue it for manual sync.
export function useDeleteTaskMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      offlineDataManager.deleteTask(taskId);
      offlineQueueManager.addOperation({ type: 'DELETE_TASK', data: { task_id: taskId, payload: {} }, maxRetries: 3 });
      return taskId;
    },
    onSuccess: () => refreshTaskCaches(qc),
  });
}

// Assignees and task-links reference server-side users/tasks and remain
// online-only. They are not queued for offline sync.
export function useAddAssigneeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
      api.addTaskAssignee(taskId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

export function useRemoveAssigneeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
      api.removeTaskAssignee(taskId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

// Offline-first: link a subtask/related task locally + queue it for manual sync.
export function useAddTaskLinkMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, linkedTaskId, linkType = 'RELATED' }: { taskId: string; linkedTaskId: string; linkType?: TaskLinkType }) => {
      // Skip if this exact link already exists (guards against double-fire).
      const current = offlineDataManager.getTasks().find(t => t.task_id === taskId);
      const already = current?.links?.find(l => l.linked_task_id === linkedTaskId && l.link_type === linkType);
      if (already) return already;

      const linkId = genLocalId();
      const linkedTask = offlineDataManager.getTasks().find(t => t.task_id === linkedTaskId);
      const link: TaskLink = {
        task_link_id: linkId,
        task_id: taskId,
        linked_task_id: linkedTaskId,
        link_type: linkType,
        created_at: new Date().toISOString(),
        linked_task: linkedTask,
      };
      offlineDataManager.addTaskLink(taskId, link);
      offlineQueueManager.addOperation({
        type: 'ADD_TASK_LINK',
        data: { task_id: taskId, linked_task_id: linkedTaskId, link_type: linkType, task_link_id: linkId },
        maxRetries: 3,
      });
      return link;
    },
    onSuccess: () => refreshTaskCaches(qc),
  });
}

// Offline-first: unlink locally + queue it for manual sync.
export function useRemoveTaskLinkMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, linkId }: { taskId: string; linkId: string }) => {
      offlineDataManager.removeTaskLink(taskId, linkId);
      offlineQueueManager.addOperation({
        type: 'REMOVE_TASK_LINK',
        data: { task_id: taskId, task_link_id: linkId },
        maxRetries: 3,
      });
      return linkId;
    },
    onSuccess: () => refreshTaskCaches(qc),
  });
}
